/**
 * RecentActivityCard Component
 *
 * Displays a timeline of recent activity/events with icons and timestamps.
 * Commonly used on dashboards to show recent actions, updates, or notifications.
 *
 * Usage:
 * <RecentActivityCard
 *   title="Recent Activity"
 *   items={[
 *     {
 *       id: '1',
 *       icon: <FileText className="w-4 h-4" />,
 *       title: 'Document uploaded',
 *       description: 'Halal Policy v2.0',
 *       timestamp: new Date(),
 *       variant: 'success'
 *     }
 *   ]}
 * />
 */

import type { ReactElement } from 'react';
import { Card, CardHeader, CardBody } from '../common/Card';

export interface ActivityItem {
  id: string;
  icon?: ReactElement;
  title: string;
  description?: string;
  timestamp: Date | string;
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'error';
  onClick?: () => void;
}

export interface RecentActivityCardProps {
  /** Card title */
  title?: string;

  /** Activity items to display */
  items: ActivityItem[];

  /** Maximum number of items to show */
  maxItems?: number;

  /** Show "View All" link */
  showViewAll?: boolean;

  /** View all click handler */
  onViewAllClick?: () => void;

  /** Loading state */
  loading?: boolean;

  /** Custom className */
  className?: string;
}

export function RecentActivityCard({
  title = 'Recent Activity',
  items,
  maxItems = 5,
  showViewAll = false,
  onViewAllClick,
  loading = false,
  className = '',
}: RecentActivityCardProps) {
  const displayItems = items.slice(0, maxItems);

  const formatTimestamp = (timestamp: Date | string) => {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;

    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const getVariantColors = (variant?: string) => {
    const colors = {
      default: 'bg-neutral-100 text-neutral-600',
      primary: 'bg-primary-100 text-primary-600',
      success: 'bg-success-100 text-success-600',
      warning: 'bg-warning-100 text-warning-600',
      error: 'bg-error-100 text-error-600',
    };
    return colors[variant as keyof typeof colors] || colors.default;
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <h3 className="text-lg font-semibold">{title}</h3>
        </CardHeader>
        <CardBody>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex gap-3 animate-pulse">
                <div className="w-10 h-10 bg-neutral-200 rounded-full shrink-0"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-neutral-200 rounded w-3/4"></div>
                  <div className="h-3 bg-neutral-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>
    );
  }

  if (items.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <h3 className="text-lg font-semibold">{title}</h3>
        </CardHeader>
        <CardBody>
          <div className="text-center py-8">
            <p className="text-neutral-500 text-sm">No recent activity</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">{title}</h3>
          {showViewAll && onViewAllClick && (
            <button
              onClick={onViewAllClick}
              className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
            >
              View All →
            </button>
          )}
        </div>
      </CardHeader>
      <CardBody>
        <div className="space-y-4">
          {displayItems.map((item, index) => (
            <div
              key={item.id}
              className={`
                flex gap-3 pb-4
                ${index < displayItems.length - 1 ? 'border-b border-neutral-100' : ''}
                ${item.onClick ? 'cursor-pointer hover:bg-neutral-50 -mx-2 px-2 py-2 rounded-lg transition-colors' : ''}
              `}
              onClick={item.onClick}
            >
              {/* Icon */}
              {item.icon && (
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${getVariantColors(item.variant)}`}>
                  {item.icon}
                </div>
              )}

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-medium text-neutral-900 text-sm">{item.title}</p>
                  <span className="text-xs text-neutral-500 shrink-0">{formatTimestamp(item.timestamp)}</span>
                </div>
                {item.description && (
                  <p className="text-sm text-neutral-600 mt-1 truncate">{item.description}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardBody>
    </Card>
  );
}

export default RecentActivityCard;
